'use client'
import ReviewStarsList from './review-stars-list'
import './product-write-review.scss'
import Form from 'next/form'
import { useActionState, useState } from 'react'
import { createReview } from '../../../actions/create-review'
import { updateReview } from '../../../actions/update-review'
import { ZodIssue } from 'zod'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import ReviewsPolicyLink from './reviews-policy-link'
import Icon from '@dustin-web/microsite-components/src/icon'
import { translateZodError } from './helpers/write-review-helpers'
import { WriteAccessStatus } from './write-access-status'

type Props = {
  productErpIdentifier: string
  reviewWriteAccess: string
  score?: number
  contactToken?: string
  reviewAuthor?: string | null | undefined
  reviewContent?: string | null | undefined
}

export type FormState =
  | { error: ZodIssue[]; success: null }
  | { error: string | null; success: null }
  | { error: boolean; success: null }
  | { error: null; success: boolean }

export default function WriteReview({
  productErpIdentifier,
  reviewWriteAccess,
  score,
  contactToken,
  reviewAuthor,
  reviewContent,
}: Props) {
  const { translate } = useTranslations()
  const [createReviewState, createReviewFormAction, isCreateReviewPending] = useActionState(
    createReview,
    {
      error: null,
      success: null,
    } as FormState
  )

  const [updateReviewState, updateReviewformAction, isUpdateReviewPending] = useActionState(
    updateReview,
    {
      error: null,
      success: null,
    } as FormState
  )

  const errorCheck = translateZodError(
    createReviewState.error ? createReviewState.error : updateReviewState.error,
    translate
  )
  const [nameValue, setNameValue] = useState<string>(reviewAuthor ? reviewAuthor : '')
  const [contentValue, setContentValue] = useState<string>(reviewContent ? reviewContent : '')
  const [checked, setChecked] = useState<boolean>(false)

  if (createReviewState.success ? createReviewState.success : updateReviewState?.success) {
    return (
      <section className="product-write-review">
        <div className="product-write-review-success">
          <Icon name="check" className="product-write-review-success__icon" />
          <div className="product-write-review-success__msg-container">
            <h2 className="product-write-review-success__thanks">
              {translate('Product_Review_Form_Success_Thanks')}
            </h2>
            <p className="product-write-review-success__msg">
              {translate('Product_Review_Form_Success_Msg')}
            </p>
          </div>
        </div>
      </section>
    )
  }

  if (reviewWriteAccess !== 'ALLOWED') {
    return <WriteAccessStatus reviewWriteAccess={reviewWriteAccess} />
  }

  return (
    <section className="product-write-review">
      <h2 className="product-write-review-form__heading">
        {translate('Product_Review_Write_Review_Heading')}
      </h2>
      <Form
        action={reviewAuthor && reviewContent ? updateReviewformAction : createReviewFormAction}
        className="product-write-review-form">
        <input type="hidden" name="productErpIdentifier" value={productErpIdentifier} />
        <input type="hidden" name="contactToken" value={contactToken} />
        <fieldset className="product-write-review-form__fieldset">
          <div className="product-write-review-form__rating-container">
            <label className="product-write-review-form__label" htmlFor="rating">
              {translate('Product_Review_Form_Rating_Heading')}
              <span className="product-write-review-form__label__asterisk"> *</span>
            </label>
            <ReviewStarsList ratingInstructions={'ratingHelp'} score={score} />
            <div>
              <p className="product-write-review-form__instructions" id="ratingHelp">
                {translate('Product_Review_Form_Rating_Instructions')}
              </p>
              {errorCheck.rating.hasError && (
                <p className="product-write-review-form__error-msg">{errorCheck.rating.msg}</p>
              )}
            </div>
          </div>

          <div className="product-write-review-form__name-container">
            <label className="product-write-review-form__label" htmlFor="fullName">
              {translate('Product_Review_Form_Name_Heading')}
              <span className="product-write-review-form__label__asterisk"> *</span>
            </label>
            <p className="product-write-review-form__instructions" id="nameHelp">
              {translate('Product_Review_Form_Name_Instructions')}
            </p>
            <div>
              <input
                aria-describedby="nameHelp"
                className={
                  errorCheck.author.hasError
                    ? 'product-write-review-form__input--error'
                    : 'product-write-review-form__input'
                }
                type="text"
                id="fullName"
                name="author"
                onChange={e => setNameValue(e.target.value)}
                value={checked ? translate('Product_Review_Form_Name_Anon') : nameValue}
                readOnly={checked}
              />
              {errorCheck.author.hasError && (
                <p className="product-write-review-form__error-msg">{errorCheck.author.msg}</p>
              )}
            </div>

            <div className="product-write-review-form__checkbox-container">
              <input
                key={checked ? 'checked' : 'unchecked'}
                aria-describedby="anonCheckHelp"
                type="checkbox"
                id="anonymous"
                name="anonymous"
                aria-label="checkbox to be anonymous"
                onChange={() => setChecked(!checked)}
                checked={checked}
              />
              <p id="anonCheckHelp" className="product-write-review-form__label--anon">
                {translate('Product_Review_Form_Anon_Checkbox')}
              </p>
            </div>
          </div>

          <div className="product-write-review-form__content-container">
            <label className="product-write-review-form__label" htmlFor="reviewText">
              {translate('Product_Review_Form_Content_Heading')}
              <span className="product-write-review-form__label__asterisk"> *</span>
            </label>
            <p className="product-write-review-form__instructions" id="writeReviewHelp">
              {translate('Product_Review_Form_Content_Instructions')}
            </p>
            <div>
              <textarea
                aria-describedby="writeReviewHelp"
                className={
                  errorCheck.content.hasError
                    ? 'product-write-review-form__textarea--error'
                    : 'product-write-review-form__textarea'
                }
                id="reviewText"
                name="content"
                onChange={e => setContentValue(e.target.value)}
                value={contentValue}
                rows={5}
              />
              {errorCheck.content.hasError && (
                <p className="product-write-review-form__error-msg">{errorCheck.content.msg}</p>
              )}
            </div>
          </div>
        </fieldset>
        <ReviewsPolicyLink />
        {(createReviewState.error || updateReviewState.error) &&
          !errorCheck.rating.hasError &&
          !errorCheck.author.hasError &&
          !errorCheck.content.hasError && (
            <div className="product-write-review-form__error-box">
              <p className="product-write-review-form__error-box__msg">
                {translate('ErrorTryAgain')}
              </p>
            </div>
          )}
        <button
          type="submit"
          aria-label="submit form button"
          className="product-write-review-form__submit-btn"
          disabled={isCreateReviewPending ? isCreateReviewPending : isUpdateReviewPending}>
          {translate('Product_Review_Form_Submit')}
        </button>
      </Form>
    </section>
  )
}
